import { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
    Shield,
    UserPlus,
    Users,
    Power,
    PowerOff,
    Pencil,
    Check,
    X,
    AlertTriangle,
    LogIn,
    Hash,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { ROLES, STATUS, isActive } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../components/ui/alert-dialog";

function initials(name) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("");
}

export default function ManageTeachers() {
    const { teachers, isAdmin, addTeacher, renameTeacher, setTeacherStatus, setWorkspaceMode } =
        useAuth();
    const { allStudents, allAttendance } = useData();
    const navigate = useNavigate();

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState("");
    const [editEmail, setEditEmail] = useState("");

    const teacherUsers = useMemo(
        () => teachers.filter((t) => t.role === ROLES.TEACHER),
        [teachers],
    );

    const studentCounts = useMemo(() => {
        const map = {};
        for (const s of allStudents) {
            map[s.teacherId] = (map[s.teacherId] || 0) + 1;
        }
        return map;
    }, [allStudents]);

    const attendanceCounts = useMemo(() => {
        const map = {};
        for (const r of allAttendance) {
            map[r.teacherId] = (map[r.teacherId] || 0) + 1;
        }
        return map;
    }, [allAttendance]);

    // Hard guard (after hooks): only admin can see this page.
    if (!isAdmin) {
        return <Navigate to="/" replace />;
    }

    const onCreate = (e) => {
        e.preventDefault();
        if (!name.trim()) {
            toast.error("Name is required");
            return;
        }
        const t = addTeacher({ name, email });
        toast.success(`${t.name} added`);
        setName("");
        setEmail("");
    };

    const startEdit = (t) => {
        setEditId(t.id);
        setEditName(t.name);
        setEditEmail(t.email || "");
    };

    const saveEdit = () => {
        if (!editName.trim()) {
            toast.error("Name cannot be empty");
            return;
        }
        renameTeacher(editId, { name: editName, email: editEmail });
        toast.success("Teacher updated");
        setEditId(null);
    };

    const onToggleActive = (t) => {
        const nextStatus = isActive(t) ? STATUS.INACTIVE : STATUS.ACTIVE;
        setTeacherStatus(t.id, nextStatus);
        toast.success(
            nextStatus === STATUS.ACTIVE
                ? `${t.name} reactivated`
                : `${t.name} deactivated`,
        );
    };

    const onSwitchInto = (t) => {
        setWorkspaceMode(t.id);
        toast.success(`Now viewing ${t.name}'s workspace`);
        navigate("/");
    };

    return (
        <div className="space-y-6" data-testid="manage-teachers-page">
            <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1 flex items-center gap-1">
                    <Shield size={11} strokeWidth={3} />
                    Admin
                </div>
                <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight">
                    Manage Teachers
                </h1>
                <p className="text-slate-500 mt-2 max-w-xl">
                    Create, rename and deactivate teachers in your studio. Every
                    student and attendance record stays tied to its teacher.
                </p>
            </div>

            {/* Create */}
            <form
                onSubmit={onCreate}
                className="bg-white rounded-3xl border border-mist card-soft p-6 sm:p-8"
                data-testid="manage-teachers-create-form"
            >
                <div className="flex items-center gap-2 mb-5">
                    <div className="w-9 h-9 rounded-xl bg-action-soft text-[#3D6385] flex items-center justify-center">
                        <UserPlus size={18} strokeWidth={2.5} />
                    </div>
                    <h2 className="font-display font-bold text-xl uppercase tracking-tight">
                        Add a teacher
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-end">
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                            Name
                        </Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Hana Park"
                            className="h-12 rounded-xl bg-mist/50 border-mist focus-visible:ring-[#3D6385]"
                            data-testid="new-teacher-name"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                            Email (optional)
                        </Label>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="hana@tempo.studio"
                            className="h-12 rounded-xl bg-mist/50 border-mist focus-visible:ring-[#3D6385]"
                            data-testid="new-teacher-email"
                        />
                    </div>
                    <Button
                        type="submit"
                        className="rounded-xl bg-[#3D6385] hover:bg-[#345570] text-white font-bold h-12 px-6 shadow-md"
                        data-testid="new-teacher-submit"
                    >
                        <UserPlus size={16} strokeWidth={2.8} className="mr-2" />
                        Add Teacher
                    </Button>
                </div>
            </form>

            {/* List */}
            <div className="bg-white rounded-3xl border border-mist card-soft overflow-hidden">
                <div className="flex items-center gap-2 px-6 sm:px-8 pt-6 pb-4">
                    <Users size={16} strokeWidth={2.5} className="text-slate-400" />
                    <h2 className="font-display font-bold text-xl uppercase tracking-tight">
                        All teachers
                    </h2>
                    <span className="text-xs font-bold text-slate-500 ml-auto">
                        {teacherUsers.length} total
                    </span>
                </div>

                <ul
                    className="divide-y divide-mist"
                    data-testid="teachers-list"
                >
                    {teacherUsers.map((t) => {
                        const inactive = !isActive(t);
                        const isEditing = editId === t.id;
                        return (
                            <li
                                key={t.id}
                                className={`px-6 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center gap-4 ${
                                    inactive ? "opacity-60" : ""
                                }`}
                                data-testid={`teacher-row-${t.id}`}
                            >
                                <div
                                    className={`w-12 h-12 rounded-2xl font-display font-bold text-base flex items-center justify-center shrink-0 ${
                                        inactive
                                            ? "bg-mist text-slate-500"
                                            : "bg-brand-soft text-brand"
                                    }`}
                                >
                                    {initials(t.name)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    {isEditing ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <Input
                                                value={editName}
                                                onChange={(e) =>
                                                    setEditName(e.target.value)
                                                }
                                                placeholder="Name"
                                                className="h-10 rounded-lg bg-mist/50 border-mist"
                                                data-testid={`edit-name-${t.id}`}
                                            />
                                            <Input
                                                value={editEmail}
                                                onChange={(e) =>
                                                    setEditEmail(e.target.value)
                                                }
                                                placeholder="Email"
                                                className="h-10 rounded-lg bg-mist/50 border-mist"
                                                data-testid={`edit-email-${t.id}`}
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-display font-bold text-lg text-slate-900">
                                                    {t.name}
                                                </span>
                                                <span
                                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                                                        inactive
                                                            ? "bg-mist text-slate-600"
                                                            : "bg-success-soft text-success"
                                                    }`}
                                                    data-testid={`status-${t.id}`}
                                                >
                                                    {inactive ? (
                                                        <PowerOff size={9} strokeWidth={3} />
                                                    ) : (
                                                        <Power size={9} strokeWidth={3} />
                                                    )}
                                                    {inactive ? "Inactive" : "Active"}
                                                </span>
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-mist/50 text-slate-500 text-[10px] font-mono tracking-wide border border-mist">
                                                    <Hash size={9} strokeWidth={3} />
                                                    {t.id}
                                                </span>
                                            </div>
                                            <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                                                {t.email && <span>{t.email}</span>}
                                                {t.email && (
                                                    <span className="text-slate-300">·</span>
                                                )}
                                                <span className="font-bold">
                                                    {studentCounts[t.id] || 0} students
                                                </span>
                                                <span className="text-slate-300">·</span>
                                                <span>
                                                    {attendanceCounts[t.id] || 0} lessons
                                                    logged
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                                    {isEditing ? (
                                        <>
                                            <Button
                                                onClick={saveEdit}
                                                className="rounded-xl bg-[#3D6385] hover:bg-[#345570] text-white font-bold h-10 px-4"
                                                data-testid={`save-edit-${t.id}`}
                                            >
                                                <Check
                                                    size={14}
                                                    strokeWidth={3}
                                                    className="mr-1"
                                                />
                                                Save
                                            </Button>
                                            <Button
                                                onClick={() => setEditId(null)}
                                                variant="outline"
                                                className="rounded-xl h-10 px-4 font-bold border-mist"
                                                data-testid={`cancel-edit-${t.id}`}
                                            >
                                                <X size={14} strokeWidth={3} />
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            {!inactive && (
                                                <Button
                                                    onClick={() => onSwitchInto(t)}
                                                    className="rounded-xl bg-brand hover:bg-brand/85 text-white font-bold h-10 px-3"
                                                    data-testid={`switch-into-${t.id}`}
                                                >
                                                    <LogIn
                                                        size={14}
                                                        strokeWidth={2.5}
                                                        className="mr-1"
                                                    />
                                                    Switch into
                                                </Button>
                                            )}
                                            <Button
                                                onClick={() => startEdit(t)}
                                                variant="outline"
                                                className="rounded-xl h-10 px-3 font-bold border-mist"
                                                data-testid={`rename-${t.id}`}
                                            >
                                                <Pencil
                                                    size={14}
                                                    strokeWidth={2.5}
                                                    className="mr-1"
                                                />
                                                Rename
                                            </Button>
                                            {inactive ? (
                                                <Button
                                                    onClick={() => onToggleActive(t)}
                                                    className="rounded-xl bg-success hover:bg-success text-white font-bold h-10 px-3"
                                                    data-testid={`reactivate-${t.id}`}
                                                >
                                                    <Power
                                                        size={14}
                                                        strokeWidth={2.5}
                                                        className="mr-1"
                                                    />
                                                    Reactivate
                                                </Button>
                                            ) : (
                                                <AlertDialog>
                                                    <AlertDialogTrigger asChild>
                                                        <Button
                                                            variant="outline"
                                                            className="rounded-xl h-10 px-3 font-bold text-danger border-danger-soft hover:bg-danger-soft hover:text-danger"
                                                            data-testid={`deactivate-${t.id}`}
                                                        >
                                                            <PowerOff
                                                                size={14}
                                                                strokeWidth={2.5}
                                                                className="mr-1"
                                                            />
                                                            Deactivate
                                                        </Button>
                                                    </AlertDialogTrigger>
                                                    <AlertDialogContent className="rounded-3xl">
                                                        <AlertDialogHeader>
                                                            <AlertDialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
                                                                <AlertTriangle
                                                                    size={18}
                                                                    className="text-warning"
                                                                />
                                                                Deactivate {t.name}?
                                                            </AlertDialogTitle>
                                                            <AlertDialogDescription>
                                                                Their <b>
                                                                    {studentCounts[t.id] || 0}
                                                                </b>{" "}
                                                                students and{" "}
                                                                <b>
                                                                    {attendanceCounts[t.id] || 0}
                                                                </b>{" "}
                                                                attendance records will be{" "}
                                                                <b>preserved</b>, but the
                                                                profile will be hidden from the
                                                                workspace switcher until
                                                                reactivated.
                                                            </AlertDialogDescription>
                                                        </AlertDialogHeader>
                                                        <AlertDialogFooter>
                                                            <AlertDialogCancel
                                                                className="rounded-xl font-bold"
                                                                data-testid={`deactivate-cancel-${t.id}`}
                                                            >
                                                                Cancel
                                                            </AlertDialogCancel>
                                                            <AlertDialogAction
                                                                onClick={() =>
                                                                    onToggleActive(t)
                                                                }
                                                                className="rounded-xl bg-danger hover:bg-danger/85 font-bold"
                                                                data-testid={`deactivate-confirm-${t.id}`}
                                                            >
                                                                Deactivate
                                                            </AlertDialogAction>
                                                        </AlertDialogFooter>
                                                    </AlertDialogContent>
                                                </AlertDialog>
                                            )}
                                        </>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}
